/**
 * Edit the weekday pattern and single-date overrides (POS2-1259 CP4).
 *
 * Two independent halves, deliberately not one form. Saving the pattern applies **from now**
 * and leaves overrides untouched (decision 2); setting a single date writes one override and
 * leaves the pattern alone (decision 3). Merging them into a single Save would make it
 * impossible to do either without implying the other.
 */
import { useEffect, useState } from "react";

import { Pencil, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import type { BaseOverride, BasePattern } from "@/hooks/use-occupancy";
import { useTranslation } from "@/i18n/use-translation";
import { WEEKDAY_LABEL_KEYS } from "@/lib/occupancy-labels";

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];

export function BaseCoversDrawer({
  open,
  pattern,
  overrides,
  hasMore,
  defaultDate,
  isSaving,
  onSavePattern,
  onSaveOverride,
  onRemoveOverride,
  onClose,
}: {
  open: boolean;
  pattern: BasePattern | null | undefined;
  /** Every entered day for the site, newest first — deliberately not the visible month, or the
   *  list would hide most of what the user has set. */
  overrides: BaseOverride[];
  /** True when the site has more entered days than the list was given. */
  hasMore: boolean;
  defaultDate: string;
  isSaving: boolean;
  onSavePattern: (covers: Record<number, number>) => void;
  onSaveOverride: (date: string, covers: number) => void;
  /** Takes the covers it is removing, so the undo toast can put the same figure back. */
  onRemoveOverride: (date: string, covers: number) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [weekly, setWeekly] = useState<Record<number, string>>({});
  const [overrideDate, setOverrideDate] = useState(defaultDate);
  const [overrideCovers, setOverrideCovers] = useState("");

  useEffect(() => {
    const seeded: Record<number, string> = {};
    for (const weekday of WEEKDAYS) {
      const day = pattern?.days.find((d) => d.weekday === weekday);
      seeded[weekday] = day ? String(day.covers) : "";
    }
    setWeekly(seeded);
  }, [pattern]);

  useEffect(() => setOverrideDate(defaultDate), [defaultDate]);

  if (!open) return null;

  const patternComplete = WEEKDAYS.every((weekday) => weekly[weekday] !== "");

  return (
    <Sheet open onOpenChange={(next) => !next && onClose()}>
      <SheetContent className="flex w-full flex-col gap-0 overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle>{t("occupancy.base.title")}</SheetTitle>
        </SheetHeader>

        <div className="flex flex-col gap-5 p-4">
          <section className="flex flex-col gap-2">
            <div>
              <h3 className="text-[13px] font-semibold">{t("occupancy.base.patternHeading")}</h3>
              <p className="text-xs text-muted-foreground">{t("occupancy.base.patternHint")}</p>
            </div>
            <div className="flex flex-col gap-1.5">
              {WEEKDAYS.map((weekday) => (
                <label key={weekday} className="flex items-center gap-3 text-[13px]">
                  <span className="w-10 text-muted-foreground">
                    {t(WEEKDAY_LABEL_KEYS[weekday])}
                  </span>
                  <Input
                    type="number"
                    min={0}
                    className="h-8"
                    value={weekly[weekday] ?? ""}
                    onChange={(e) =>
                      setWeekly((current) => ({ ...current, [weekday]: e.target.value }))
                    }
                  />
                </label>
              ))}
            </div>
            <Button
              className="self-start"
              disabled={!patternComplete || isSaving}
              onClick={() =>
                onSavePattern(Object.fromEntries(WEEKDAYS.map((w) => [w, Number(weekly[w] || 0)])))
              }
            >
              {t("occupancy.action.save")}
            </Button>
          </section>

          <section className="flex flex-col gap-2 border-t border-border pt-4">
            <h3 className="text-[13px] font-semibold">{t("occupancy.base.overrideHeading")}</h3>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-1 text-[13px]">
                {t("occupancy.base.overrideDate")}
                <Input
                  type="date"
                  value={overrideDate}
                  onChange={(e) => setOverrideDate(e.target.value)}
                />
              </label>
              <label className="flex flex-col gap-1 text-[13px]">
                {t("occupancy.base.overrideCovers")}
                <Input
                  type="number"
                  min={0}
                  value={overrideCovers}
                  onChange={(e) => setOverrideCovers(e.target.value)}
                />
              </label>
            </div>
            <Button
              variant="outline"
              className="self-start"
              disabled={overrideCovers === "" || isSaving}
              onClick={() => {
                onSaveOverride(overrideDate, Number(overrideCovers));
                setOverrideCovers("");
              }}
            >
              {t("occupancy.base.setForDate")}
            </Button>
          </section>

          <section className="flex flex-col gap-2 border-t border-border pt-4">
            <div>
              <h3 className="text-[13px] font-semibold">{t("occupancy.base.overridesHeading")}</h3>
              {/* Without this line the pattern above looks broken: a date with its own figure
                  keeps it, so editing the weekday value appears to do nothing on exactly the
                  days somebody has already touched — including every day the homepage widget
                  wrote when it split a week (POS2-1259 decision 3). */}
              <p className="text-xs text-muted-foreground">{t("occupancy.base.overridesHint")}</p>
            </div>
            {overrides.length === 0 ? (
              <p className="text-xs text-muted-foreground">{t("occupancy.base.noOverrides")}</p>
            ) : (
              // Capped height: a site backfilled from the old platform can hold hundreds of
              // entered days, and an unbounded list would push Save off the screen.
              <ul className="flex max-h-64 flex-col gap-0.5 overflow-y-auto">
                {overrides.map((override) => (
                  <li
                    key={override.date}
                    className="flex items-center gap-2 rounded-md px-1.5 py-1 text-[13px] hover:bg-muted/50"
                  >
                    <span className="tabular-nums font-medium">{override.covers}</span>
                    <span className="tabular-nums text-muted-foreground">{override.date}</span>
                    {/* Which surface wrote it. A weekly-entered day is one seventh of a figure
                        somebody gave the widget, and saying so is the difference between "I
                        edited this" and "this came from the week I saved". */}
                    {override.source !== "page" && (
                      <span className="rounded border border-border px-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {t("occupancy.base.weeklyTag")}
                      </span>
                    )}
                    <span className="ml-auto flex items-center gap-0.5">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7"
                        aria-label={t("occupancy.base.edit")}
                        // Loads the day back into the form above rather than editing in place:
                        // one input pair, one Save, and the form already handles the upsert.
                        onClick={() => {
                          setOverrideDate(override.date);
                          setOverrideCovers(String(override.covers));
                        }}
                      >
                        <Pencil className="size-3.5 text-muted-foreground" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-7"
                        aria-label={t("occupancy.base.removeDay")}
                        onClick={() => onRemoveOverride(override.date, override.covers)}
                      >
                        <Trash2 className="size-3.5 text-muted-foreground" />
                      </Button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {hasMore && (
              <p className="text-xs text-muted-foreground">{t("occupancy.base.moreDays")}</p>
            )}
          </section>
        </div>
      </SheetContent>
    </Sheet>
  );
}
